import React from 'react';
import { Button, Space, Input, message } from 'antd';
import { FileAddOutlined, FolderAddOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import './style.css';
import { AkukoAPIService } from '../../../../../../../services/serviceClass';
import { POSTS_API } from '../../../../../../../configs/env';
import { trackEvent } from '../../../../../../../configs/posthog';
import { ERROR_GENERIC } from '../../../../../../../configs/constants';
import { Dictionary } from '@onaio/utils';
import { PostFolder } from '../../../../../../../types/postFolders';

interface SpaceHeaderProps {
  canEdit: boolean;
  isSpaceAdmin: boolean;
  accountId: string;
  spaceId: string;
  selectedFolder: PostFolder | null;
  onCreateFolder: () => void;
  onSearch: (searchText: string) => void;
  onShareFolder: () => void;
  user: Dictionary;
}

const SpaceHeader: React.FC<SpaceHeaderProps> = ({
  canEdit,
  isSpaceAdmin,
  accountId,
  spaceId,
  user,
  selectedFolder,
  onCreateFolder,
  onSearch,
  onShareFolder,
}) => {
  const history = useHistory();

  const getFolderPermissions = () => {
    if (!selectedFolder) {
      return { canEdit: canEdit, canCreate: isSpaceAdmin };
    }

    const permissions = selectedFolder.metadata?.permissions || {};
    return {
      canEdit: permissions.edit || permissions.admin || false,
      canCreate: permissions.admin || false
    };
  };

  const { canEdit: hasEditAccess, canCreate: hasCreateAccess } = getFolderPermissions();

  // If no permissions at all, show only search bar
  if (!hasEditAccess && !hasCreateAccess) {
    return (
      <div className="space-posts-header">
        <div className="search-section">
          <Input
            placeholder="Search folders and posts"
            prefix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
            onChange={(e) => onSearch(e.target.value)}
            allowClear
          />
        </div>
        {selectedFolder && (
          <div className="actions-section">
            <Button 
              icon={<TeamOutlined />}
              onClick={onShareFolder}
            >
              Share
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-posts-header">
      <div className="search-section">
        <Input
          placeholder="Search folders and posts"
          prefix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
          onChange={(e) => onSearch(e.target.value)}
          allowClear
        />
      </div>
      <div className="actions-section">
        <Space size="middle">
          {hasEditAccess && (
            <Button
              type="primary"
              icon={<FileAddOutlined />}
              onClick={() => {
                const service = new AkukoAPIService(POSTS_API, 'post');
                service
                  .create({
                    account_id: accountId,
                    space_id: spaceId,
                    title: 'My new post',
                    folder_id: selectedFolder?.id,
                    edit: user.email,
                    author: user.email,
                    components: [
                      {
                        type: 'title',
                        description: '',
                      },
                    ],
                    data: {},
                    sources: {},
                  })
                  .then((res) => {
                    const response = res as Dictionary
                    trackEvent('Create Post', { post: response.uuid });
                    history.push(`/post/${response.uuid}/edit`);
                  })
                  .catch((error) => {
                    message.error(error.message || ERROR_GENERIC);
                  });
              }}>
              New Post
            </Button>
          )}
          {hasCreateAccess && (
            <Button icon={<FolderAddOutlined />} onClick={onCreateFolder}>
              New Folder
            </Button>
          )}
          {selectedFolder && (
            <Button 
              icon={<TeamOutlined />}
              onClick={onShareFolder}
            >
              Share
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default SpaceHeader; 